/**
 * Purchase Tracking Utilities for GA4 and GTM
 *
 * This module provides utilities to track purchase events properly in Google Analytics 4
 * and Google Tag Manager with consistent data structure and proper ecommerce tracking.
 *
 * IMPORTANT: All tracking functions are wrapped in try-catch blocks to ensure
 * analytics failures never break application functionality.
 */

// Declare global types for tracking
declare global {
	interface Window {
		gtag?: (...args: any[]) => void;
		dataLayer?: Object[];
	}
}

export interface PurchaseItem {
	item_id: string;
	item_name: string;
	category: string;
	quantity: number;
	price: number;
}

export interface PurchaseEventData {
	transaction_id: string;
	value: number;
	currency: string;
	items: PurchaseItem[];
	purchase_type: 'domain' | 'email_plan' | 'other';
	user_id?: string;
	// Additional custom properties can be added here
	[key: string]: any;
}

/**
 * Safe wrapper for all tracking operations
 * Ensures analytics never breaks application functionality
 */
function safeTrack(operation: string, trackingFunction: () => void): void {
	try {
		// Only run on client-side
		if (typeof window === 'undefined') {
			return;
		}

		trackingFunction();
	} catch (error) {
		// Log error but never throw - analytics should never break the app
		console.warn(`[Analytics] Failed to track ${operation}:`, error);
	}
}

/**
 * Track a purchase event using both GA4 (gtag) and GTM DataLayer
 *
 * @param data - Purchase event data conforming to GA4 ecommerce standards
 */
export function trackPurchaseEvent(data: PurchaseEventData): void {
	safeTrack('purchase event', () => {
		const {
			transaction_id,
			value,
			currency,
			items,
			purchase_type,
			user_id,
			...customProperties
		} = data;

		// Validate required fields
		if (!transaction_id || !value || !currency || !items?.length) {
			console.warn('[Analytics] Purchase tracking skipped: Missing required fields', {
				transaction_id: !!transaction_id,
				value: !!value,
				currency: !!currency,
				items_count: items?.length || 0
			});
			return;
		}

		// Track with Google Analytics 4 (gtag) - primary method
		if (typeof window !== 'undefined' && window.gtag) {
			try {
				window.gtag('event', 'purchase', {
					transaction_id,
					value,
					currency,
					items: items.map((item) => ({
						item_id: item.item_id,
						item_name: item.item_name,
						category: item.category,
						quantity: item.quantity,
						price: item.price
					}))
				});

				console.log('✅ GA4 Purchase tracked:', {
					transaction_id,
					value,
					currency,
					purchase_type,
					items_count: items.length
				});
			} catch (gtagError) {
				console.warn('[Analytics] GA4 tracking failed:', gtagError);
			}
		} else {
			console.debug('[Analytics] GA4 (gtag) not available for purchase tracking');
		}

		// Track with Google Tag Manager DataLayer (backup/additional method)
		if (typeof window !== 'undefined') {
			try {
				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					event: 'purchase',
					ecommerce: {
						transaction_id,
						value,
						currency,
						items: items.map((item) => ({
							item_id: item.item_id,
							item_name: item.item_name,
							item_category: item.category,
							quantity: item.quantity,
							price: item.price
						}))
					},
					// Add custom properties
					purchase_type,
					user_id: user_id || 'anonymous',
					...customProperties
				});

				console.log('✅ GTM Purchase tracked:', {
					transaction_id,
					value,
					currency,
					purchase_type,
					items_count: items.length
				});
			} catch (gtmError) {
				console.warn('[Analytics] GTM tracking failed:', gtmError);
			}
		}
	});
}

/**
 * Track domain purchase specifically
 * Guaranteed to never throw errors or break functionality
 */
export function trackDomainPurchase(
	domain: string,
	price: number,
	transactionId?: string,
	userId?: string
): void {
	safeTrack('domain purchase', () => {
		const finalTransactionId = transactionId || `domain_${domain}_${Date.now()}`;

		trackPurchaseEvent({
			transaction_id: finalTransactionId,
			value: price,
			currency: 'USD',
			items: [
				{
					item_id: `domain_${domain}`,
					item_name: `Domain Registration: ${domain}`,
					category: 'Domain',
					quantity: 1,
					price: price
				}
			],
			purchase_type: 'domain',
			domain_name: domain,
			user_id: userId
		});
	});
}

/**
 * Track email plan purchase specifically
 * Guaranteed to never throw errors or break functionality
 */
export function trackEmailPlanPurchase(
	siteId: string,
	planName: string,
	price: number,
	isProrated: boolean = false,
	prorationDetails?: {
		originalAmount: number;
		chargeAmount: number;
		unusedCredit: number;
	},
	transactionId?: string,
	userId?: string
): void {
	safeTrack('email plan purchase', () => {
		const finalTransactionId = transactionId || `email_plan_${siteId}_${Date.now()}`;
		const finalPlanName = isProrated ? `${planName} (Upgrade)` : planName;

		const customProperties: Record<string, any> = {
			plan_type: planName,
			is_prorated: isProrated
		};

		if (isProrated && prorationDetails) {
			customProperties.original_amount = prorationDetails.originalAmount;
			customProperties.charge_amount = prorationDetails.chargeAmount;
			customProperties.unused_credit = prorationDetails.unusedCredit;
		}

		trackPurchaseEvent({
			transaction_id: finalTransactionId,
			value: price,
			currency: 'USD',
			items: [
				{
					item_id: `email_plan_${siteId}`,
					item_name: finalPlanName,
					category: 'Email Plans',
					quantity: 1,
					price: price
				}
			],
			purchase_type: 'email_plan',
			user_id: userId,
			...customProperties
		});
	});
}

/**
 * Generate a unique transaction ID
 * Guaranteed to never throw errors
 */
export function generateTransactionId(prefix: string = 'txn'): string {
	try {
		const timestamp = Date.now();
		const random = Math.random().toString(36).substring(2, 8);
		return `${prefix}_${timestamp}_${random}`;
	} catch (error) {
		// Fallback if anything goes wrong
		console.warn('[Analytics] Transaction ID generation failed, using fallback:', error);
		return `${prefix}_${Date.now()}_fallback`;
	}
}

/**
 * Check if tracking is available (for debugging purposes)
 * Guaranteed to never throw errors
 */
export function isTrackingAvailable(): {
	gtag: boolean;
	dataLayer: boolean;
} {
	try {
		return {
			gtag: typeof window !== 'undefined' && typeof window.gtag === 'function',
			dataLayer: typeof window !== 'undefined' && Array.isArray(window.dataLayer)
		};
	} catch (error) {
		console.warn('[Analytics] Tracking availability check failed:', error);
		return {
			gtag: false,
			dataLayer: false
		};
	}
}
